/**
 * Copyright (c) 2018-2019 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */
import { Unit } from '../../../../mol-model/structure';
import { Segmentation, OrderedSet, Interval, SortedArray } from '../../../../mol-data/int';
import { SortedRanges } from '../../../../mol-data/int/sorted-ranges';
import { isNucleic, isProtein } from '../../../../mol-model/structure/model/types';
export function getAtomicPolymerElements(unit) {
  var indices = [];
  var elements = unit.elements,
      model = unit.model;
  var residueAtomSegments = unit.model.atomicHierarchy.residueAtomSegments;
  var traceElementIndex = model.atomicHierarchy.derived.residue.traceElementIndex;
  var polymerIt = SortedRanges.transientSegments(unit.model.atomicRanges.polymerRanges, elements);
  var residueIt = Segmentation.transientSegments(residueAtomSegments, elements);

  while (polymerIt.hasNext) {
    var polymerSegment = polymerIt.move();
    residueIt.setSegment(polymerSegment);

    while (residueIt.hasNext) {
      var residueSegment = residueIt.move();
      var start = residueSegment.start,
          end = residueSegment.end,
          index = residueSegment.index;

      if (OrderedSet.areIntersecting(Interval.ofRange(elements[start], elements[end - 1]), elements)) {
        var elementIndex = traceElementIndex[index];
        indices.push(elementIndex === -1 ? residueAtomSegments.offsets[index] : elementIndex);
      }
    }
  }

  return SortedArray.ofSortedArray(indices);
}
export function getCoarsePolymerElements(unit) {
  var indices = [];
  var elements = unit.elements,
      model = unit.model;
  var _a = model.coarseHierarchy,
      spheres = _a.spheres,
      gaussians = _a.gaussians;
  var polymerRanges = Unit.isSpheres(unit) ? spheres.polymerRanges : gaussians.polymerRanges;
  var polymerIt = SortedRanges.transientSegments(polymerRanges, elements);

  while (polymerIt.hasNext) {
    var _b = polymerIt.move(),
        start = _b.start,
        end = _b.end;

    for (var i = start; i < end; ++i) {
      indices.push(elements[i]);
    }
  }

  return SortedArray.ofSortedArray(indices);
} //

export function getAtomicGapElements(unit) {
  var indices = [];
  var elements = unit.elements,
      model = unit.model,
      residueIndex = unit.residueIndex;
  var residueAtomSegments = unit.model.atomicHierarchy.residueAtomSegments;
  var traceElementIndex = model.atomicHierarchy.derived.residue.traceElementIndex;
  var gapIt = SortedRanges.transientSegments(unit.model.atomicRanges.gapRanges, unit.elements);

  while (gapIt.hasNext) {
    var gapSegment = gapIt.move();
    var indexStart = residueIndex[elements[gapSegment.start]];
    var indexEnd = residueIndex[elements[gapSegment.end - 1]];
    var elementIndexStart = traceElementIndex[indexStart];
    var elementIndexEnd = traceElementIndex[indexEnd];
    indices.push(elementIndexStart === -1 ? residueAtomSegments.offsets[indexStart] : elementIndexStart);
    indices.push(elementIndexEnd === -1 ? residueAtomSegments.offsets[indexEnd] : elementIndexEnd);
  }

  return SortedArray.ofSortedArray(indices);
}
export function getCoarseGapElements(unit) {
  var indices = [];
  var elements = unit.elements,
      model = unit.model;
  var _a = model.coarseHierarchy,
      spheres = _a.spheres,
      gaussians = _a.gaussians;
  var gapRanges = Unit.isSpheres(unit) ? spheres.gapRanges : gaussians.gapRanges;
  var gapIt = SortedRanges.transientSegments(gapRanges, elements);

  while (gapIt.hasNext) {
    var _b = gapIt.move(),
        start = _b.start,
        end = _b.end;

    indices.push(elements[start], elements[end - 1]);
  }

  return SortedArray.ofSortedArray(indices);
} //

export function getNucleotideElements(unit) {
  var indices = [];
  var elements = unit.elements,
      model = unit.model;
  var _a = model.atomicHierarchy,
      chainAtomSegments = _a.chainAtomSegments,
      residueAtomSegments = _a.residueAtomSegments;
  var _b = model.atomicHierarchy.derived.residue,
      moleculeType = _b.moleculeType,
      traceElementIndex = _b.traceElementIndex;
  var chainIt = Segmentation.transientSegments(chainAtomSegments, elements);
  var residueIt = Segmentation.transientSegments(residueAtomSegments, elements);

  while (chainIt.hasNext) {
    residueIt.setSegment(chainIt.move());

    while (residueIt.hasNext) {
      var index = residueIt.move().index;

      if (isNucleic(moleculeType[index])) {
        var elementIndex = traceElementIndex[index];
        indices.push(elementIndex === -1 ? residueAtomSegments.offsets[index] : elementIndex);
      }
    }
  }

  return SortedArray.ofSortedArray(indices);
}
export function getProteinElements(unit) {
  var indices = [];
  var elements = unit.elements,
      model = unit.model;
  var _a = model.atomicHierarchy,
      chainAtomSegments = _a.chainAtomSegments,
      residueAtomSegments = _a.residueAtomSegments;
  var _b = model.atomicHierarchy.derived.residue,
      moleculeType = _b.moleculeType,
      traceElementIndex = _b.traceElementIndex;
  var chainIt = Segmentation.transientSegments(chainAtomSegments, elements);
  var residueIt = Segmentation.transientSegments(residueAtomSegments, elements);

  while (chainIt.hasNext) {
    residueIt.setSegment(chainIt.move());

    while (residueIt.hasNext) {
      var index = residueIt.move().index;

      if (isProtein(moleculeType[index])) {
        var elementIndex = traceElementIndex[index];
        indices.push(elementIndex === -1 ? residueAtomSegments.offsets[index] : elementIndex);
      }
    }
  }

  return SortedArray.ofSortedArray(indices);
}